import { z } from "zod";
import { BOARD_MEETING_STATUS, BOARD_ASSET_STATUS } from "../constants/board-states.js";
const boardMeetingStatusEnum = z.enum([
    BOARD_MEETING_STATUS.DRAFT,
    BOARD_MEETING_STATUS.PUBLISHED,
    BOARD_MEETING_STATUS.IN_PROGRESS,
    BOARD_MEETING_STATUS.COMPLETED,
    BOARD_MEETING_STATUS.ARCHIVED,
]);
const boardAssetStatusEnum = z.enum([
    BOARD_ASSET_STATUS.DRAFT,
    BOARD_ASSET_STATUS.FINAL,
    BOARD_ASSET_STATUS.SUPERSEDED,
]);
// ─── Agenda ────────────────────────────────────────────────
export const AgendaItemSchema = z.object({
    ref: z.string().min(1).max(10),
    title: z.string().min(1).max(200),
    owner: z.string().max(100).optional(),
    dataSource: z.string().max(100).optional(),
    notes: z.string().max(5000).optional(),
});
export const AgendaSectionSchema = z.object({
    ref: z.string().min(1).max(10),
    title: z.string().min(1).max(200),
    purpose: z.string().max(500).optional(),
    items: z.array(AgendaItemSchema).default([]),
});
// ─── Actions ───────────────────────────────────────────────
export const BoardActionSchema = z.object({
    id: z.string().uuid(),
    ref: z.string().max(10),
    description: z.string().min(1).max(1000),
    owner: z.string().max(100),
    dueDate: z.string().datetime().nullable().optional(),
    status: z.enum(["open", "carried_forward", "completed"]).default("open"),
    originMeetingId: z.string().uuid().optional(),
});
export const BoardActionCreateSchema = BoardActionSchema.omit({ id: true });
export const BoardActionUpdateSchema = z.object({
    status: z.enum(["open", "carried_forward", "completed"]).optional(),
    description: z.string().min(1).max(1000).optional(),
    owner: z.string().max(100).optional(),
    dueDate: z.string().datetime().nullable().optional(),
});
// ─── Board Meeting ─────────────────────────────────────────
export const BoardMeetingSchema = z.object({
    id: z.string().uuid(),
    title: z.string().min(1).max(200),
    date: z.string().datetime(),
    location: z.string().max(200).optional(),
    chair: z.string().max(100).optional(),
    attendees: z.string().max(2000).optional(),
    status: boardMeetingStatusEnum,
    agenda: z.array(AgendaSectionSchema).default([]),
    actions: z.array(BoardActionSchema).default([]),
    vaultDocumentIds: z.array(z.string().uuid()).max(40).default([]),
    previousMeetingId: z.string().uuid().nullable().default(null),
    currentSnapshotId: z.string().uuid().nullable().default(null),
    currentCommentaryId: z.string().uuid().nullable().default(null),
    currentPackId: z.string().uuid().nullable().default(null),
    createdBy: z.string().email(),
    archivedAt: z.string().datetime().nullable().default(null),
    archivedBy: z.string().email().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardMeetingListQuerySchema = z.object({
    // Admin-only opt-ins. Server enforces; non-admins always see active-only.
    includeArchived: z.coerce.boolean().optional(),
    archivedOnly: z.coerce.boolean().optional(),
});
export const BoardMeetingCreateSchema = z.object({
    title: z.string().min(1).max(200),
    date: z.string().datetime(),
    location: z.string().max(200).optional(),
    chair: z.string().max(100).optional(),
    attendees: z.string().max(2000).optional(),
    agenda: z.array(AgendaSectionSchema).default([]),
    previousMeetingId: z.string().uuid().optional(),
    vaultDocumentIds: z.array(z.string().uuid()).max(40).optional(),
});
export const BoardMeetingUpdateSchema = BoardMeetingCreateSchema.partial();
// ─── Assets (snapshots, commentary, packs) ─────────────────
export const BoardAssetSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    type: z.enum(["snapshot", "commentary", "pack"]),
    version: z.number().int().positive(),
    status: boardAssetStatusEnum,
    data: z.record(z.string(), z.unknown()),
    blobUrl: z.string().url().optional(),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
});
// ─── Board Pack Builder ───────────────────────────────────
export const BOARD_PACK_SECTION_KEYS = [
    "cover-agenda",
    "ceo-letter",
    "kpi-scorecard",
    "financial-performance",
    "commercial-pipeline",
    "product-roadmap",
    "people-update",
    "strategic-discussion",
    "risk-register",
    "resolutions-required",
    "action-log",
    "forward-calendar",
];
export const BoardPackSectionKeySchema = z.enum(BOARD_PACK_SECTION_KEYS);
export const BoardPackSectionStatusSchema = z.enum(["not_started", "draft", "ready", "signed_off"]);
export const BoardMemoStatusSchema = z.enum(["draft", "ready", "approved"]);
export const BoardMemoTypeSchema = z.enum(["decision", "discussion", "update", "approval"]);
export const BoardMemoRequestedOutcomeSchema = z.enum([
    "approve",
    "advise",
    "choose_option",
    "challenge",
    "note",
]);
export const BoardMemoQualityFlagSchema = z.enum([
    "missing_question",
    "missing_requested_outcome",
    "missing_recommendation",
    "missing_risks",
    "missing_evidence",
]);
export const BoardResolutionStatusSchema = z.enum(["proposed", "approved", "rejected", "deferred"]);
export const BoardCirculationDeliveryStatusSchema = z.enum(["sent", "failed"]);
export const BoardMinutesStatusSchema = z.enum(["draft", "approved"]);
export const BoardMemoOptionSchema = z.object({
    label: z.string().min(1).max(120),
    summary: z.string().max(5000).default(""),
    pros: z.array(z.string().max(1000)).max(20).default([]),
    cons: z.array(z.string().max(1000)).max(20).default([]),
});
export const BoardMemoSourceLinkSchema = z.object({
    label: z.string().min(1).max(300),
    href: z.string().min(1).max(2000),
    kind: z.string().min(1).max(40),
    nodeId: z.string().min(1).max(300).optional(),
});
export const BoardPackSectionSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    sectionKey: z.string().min(1).max(80),
    sortOrder: z.number().int().nonnegative(),
    title: z.string().min(1).max(200),
    purpose: z.string().nullable(),
    required: z.boolean(),
    ownerEmail: z.string().email().nullable(),
    status: BoardPackSectionStatusSchema,
    body: z.string(),
    generatedPayload: z.record(z.string(), z.unknown()),
    signedOffBy: z.string().email().nullable(),
    signedOffAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardPackSectionUpdateSchema = z.object({
    title: z.string().min(1).max(200).optional(),
    purpose: z.string().max(5000).nullable().optional(),
    ownerEmail: z.string().email().nullable().optional(),
    status: BoardPackSectionStatusSchema.optional(),
    body: z.string().max(100_000).optional(),
    generatedPayload: z.record(z.string(), z.unknown()).optional(),
    signedOffBy: z.string().email().nullable().optional(),
});
export const BoardMemoSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    sectionId: z.string().uuid().nullable(),
    title: z.string().min(1).max(200),
    question: z.string().min(1),
    memoType: BoardMemoTypeSchema.default("decision"),
    requestedOutcome: BoardMemoRequestedOutcomeSchema.nullable().default(null),
    decisionOwnerEmail: z.string().email().nullable().default(null),
    background: z.string(),
    managementView: z.string(),
    options: z.array(BoardMemoOptionSchema).default([]),
    recommendation: z.string().default(""),
    risks: z.array(z.string()),
    evidenceDocumentIds: z.array(z.string().uuid()).default([]),
    sourceLinks: z.array(BoardMemoSourceLinkSchema).default([]),
    qualityFlags: z.array(BoardMemoQualityFlagSchema).default([]),
    decisionSought: z.string().min(1),
    ownerEmail: z.string().email().nullable(),
    status: BoardMemoStatusSchema,
    readyForBoardAt: z.string().datetime().nullable().default(null),
    approvedAt: z.string().datetime().nullable().default(null),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardMemoCreateSchema = z.object({
    sectionId: z.string().uuid().nullable().optional(),
    title: z.string().min(1).max(200),
    question: z.string().min(1).max(5000),
    memoType: BoardMemoTypeSchema.default("decision"),
    requestedOutcome: BoardMemoRequestedOutcomeSchema.nullable().optional(),
    decisionOwnerEmail: z.string().email().nullable().optional(),
    background: z.string().max(50_000).default(""),
    managementView: z.string().max(50_000).default(""),
    options: z.array(BoardMemoOptionSchema).max(8).default([]),
    recommendation: z.string().max(50_000).default(""),
    risks: z.array(z.string().max(1000)).max(20).default([]),
    evidenceDocumentIds: z.array(z.string().uuid()).max(40).default([]),
    sourceLinks: z.array(BoardMemoSourceLinkSchema).max(20).default([]),
    decisionSought: z.string().min(1).max(5000),
    ownerEmail: z.string().email().nullable().optional(),
    status: BoardMemoStatusSchema.default("draft"),
});
export const BoardMemoUpdateSchema = BoardMemoCreateSchema.partial();
export const BoardMemoMarkReadySchema = z.object({});
export const BoardMemoApproveSchema = z.object({});
export const BoardMemoResolutionCreateSchema = z.object({
    proposerEmail: z.string().email().nullable().optional(),
    evidenceDocumentId: z.string().uuid().nullable().optional(),
});
export const BoardMemoActionCreateSchema = z.object({
    owner: z.string().min(1).max(100),
    dueDate: z.string().datetime().optional(),
});
export const BoardResolutionSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    text: z.string().min(1),
    proposerEmail: z.string().email().nullable(),
    status: BoardResolutionStatusSchema,
    outcomeNotes: z.string().nullable(),
    evidenceDocumentId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardResolutionCreateSchema = z.object({
    text: z.string().min(1).max(10_000),
    proposerEmail: z.string().email().nullable().optional(),
    status: BoardResolutionStatusSchema.default("proposed"),
    outcomeNotes: z.string().max(10_000).nullable().optional(),
    evidenceDocumentId: z.string().uuid().nullable().optional(),
});
export const BoardResolutionUpdateSchema = BoardResolutionCreateSchema.partial();
export const BoardPaperSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    sectionKey: z.string().max(80).nullable(),
    vaultDocumentId: z.string().uuid(),
    purpose: z.string().nullable(),
    ownerEmail: z.string().email().nullable(),
    includeInPack: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardPaperCreateSchema = z.object({
    sectionKey: z.string().min(1).max(80).nullable().optional(),
    vaultDocumentId: z.string().uuid(),
    purpose: z.string().max(5000).nullable().optional(),
    ownerEmail: z.string().email().nullable().optional(),
    includeInPack: z.boolean().default(true),
});
export const BoardPaperUpdateSchema = BoardPaperCreateSchema.partial();
export const BoardCirculationAnnotationSchema = z.object({
    id: z.string().uuid(),
    boardCirculationId: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    boardAssetId: z.string().uuid().nullable(),
    authorEmail: z.string().email(),
    body: z.string().min(1),
    pageNumber: z.number().int().positive().nullable(),
    sectionKey: z.string().max(80).nullable(),
    anchorText: z.string().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardCirculationAnnotationCreateSchema = z.object({
    body: z.string().trim().min(1).max(5000),
    pageNumber: z.number().int().positive().nullable().optional(),
    sectionKey: z.string().trim().min(1).max(80).nullable().optional(),
    anchorText: z.string().trim().min(1).max(1000).nullable().optional(),
});
export const BoardCirculationSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    boardAssetId: z.string().uuid().nullable(),
    recipientEmail: z.string().email(),
    recipientName: z.string().max(200).nullable(),
    deliveryStatus: BoardCirculationDeliveryStatusSchema,
    note: z.string().nullable(),
    sentAt: z.string().datetime().nullable(),
    readAt: z.string().datetime().nullable(),
    downloadedAt: z.string().datetime().nullable(),
    acknowledgedAt: z.string().datetime().nullable(),
    annotations: z.array(BoardCirculationAnnotationSchema).default([]),
    createdBy: z.string().email(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardCirculationRecipientSchema = z.object({
    email: z.string().email(),
    name: z.string().min(1).max(200).optional(),
});
export const BoardCirculationCreateSchema = z.object({
    boardAssetId: z.string().uuid().optional(),
    recipients: z.array(BoardCirculationRecipientSchema).min(1).max(50),
    note: z.string().max(5000).nullable().optional(),
});
export const BoardCirculationListResponseSchema = z.object({
    circulations: z.array(BoardCirculationSchema),
});
export const BoardCirculationAnnotationListResponseSchema = z.object({
    annotations: z.array(BoardCirculationAnnotationSchema),
});
export const BoardCirculationMarkSchema = z.object({});
export const BoardMinutesSchema = z.object({
    id: z.string().uuid(),
    boardMeetingId: z.string().uuid(),
    summary: z.string(),
    decisions: z.array(z.string()),
    actionReview: z.array(z.string()),
    status: BoardMinutesStatusSchema,
    recordedBy: z.string().email(),
    approvedBy: z.string().email().nullable(),
    approvedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
export const BoardMinutesUpsertSchema = z.object({
    summary: z.string().min(1).max(100_000),
    decisions: z.array(z.string().min(1).max(5000)).max(100).default([]),
    actionReview: z.array(z.string().min(1).max(5000)).max(100).default([]),
    status: BoardMinutesStatusSchema.default("draft"),
});
export const BoardMinutesApproveSchema = z.object({});
export const BoardPackSectionParamSchema = z.object({
    id: z.string().uuid(),
    sectionKey: z.string().min(1).max(80),
});
export const BoardMemoParamSchema = z.object({
    id: z.string().uuid(),
    memoId: z.string().uuid(),
});
export const BoardResolutionParamSchema = z.object({
    id: z.string().uuid(),
    resolutionId: z.string().uuid(),
});
export const BoardPaperParamSchema = z.object({
    id: z.string().uuid(),
    paperId: z.string().uuid(),
});
export const BoardActionParamSchema = z.object({
    id: z.string().uuid(),
    actionId: z.string().uuid(),
});
export const BoardCirculationParamSchema = z.object({
    id: z.string().uuid(),
    circulationId: z.string().uuid(),
});
export const BoardPackCompletenessSchema = z.object({
    totalSections: z.number().int().nonnegative(),
    readySections: z.number().int().nonnegative(),
    signedOffSections: z.number().int().nonnegative(),
    missingRequiredSections: z.array(z.string()),
    hasDecisionMemo: z.boolean(),
    hasResolution: z.boolean(),
    readyForPack: z.boolean(),
});
export const BoardPackManifestEntryKindSchema = z.enum([
    "meeting",
    "section",
    "memo",
    "resolution",
    "paper",
    "action",
]);
export const BoardPackContentManifestEntrySchema = z.object({
    kind: BoardPackManifestEntryKindSchema,
    key: z.string().min(1).max(240),
    title: z.string().min(1).max(500),
    status: z.string().max(80).nullable().default(null),
    sectionKey: z.string().max(80).nullable().default(null),
    contentHash: z.string().min(16).max(128),
    sourceIds: z.array(z.string().min(1).max(240)).default([]),
    includedInPack: z.boolean().default(true),
});
export const BoardPackContentManifestSchema = z.object({
    meetingId: z.string().uuid(),
    generatedAt: z.string().datetime(),
    entries: z.array(BoardPackContentManifestEntrySchema),
    summary: z.object({
        sections: z.number().int().nonnegative(),
        memos: z.number().int().nonnegative(),
        resolutions: z.number().int().nonnegative(),
        papers: z.number().int().nonnegative(),
        actions: z.number().int().nonnegative(),
    }),
    completeness: BoardPackCompletenessSchema,
});
export const BoardPackVersionDiffQuerySchema = z.object({
    fromAssetId: z.string().uuid().optional(),
    toAssetId: z.string().uuid().optional(),
});
export const BoardPackVersionDiffAssetSchema = z.object({
    id: z.string().uuid(),
    version: z.number().int().positive(),
    createdAt: z.string().datetime(),
    manifestAvailable: z.boolean(),
});
export const BoardPackVersionDiffChangeSchema = z.object({
    changeType: z.enum(["added", "removed", "changed"]),
    kind: BoardPackManifestEntryKindSchema,
    key: z.string().min(1).max(240),
    title: z.string().min(1).max(500),
    sectionKey: z.string().max(80).nullable(),
    fromStatus: z.string().max(80).nullable(),
    toStatus: z.string().max(80).nullable(),
    fromContentHash: z.string().min(16).max(128).nullable(),
    toContentHash: z.string().min(16).max(128).nullable(),
});
export const BoardPackVersionDiffSchema = z.object({
    meetingId: z.string().uuid(),
    fromAsset: BoardPackVersionDiffAssetSchema.nullable(),
    toAsset: BoardPackVersionDiffAssetSchema,
    summary: z.object({
        added: z.number().int().nonnegative(),
        removed: z.number().int().nonnegative(),
        changed: z.number().int().nonnegative(),
        unchanged: z.number().int().nonnegative(),
    }),
    changes: z.array(BoardPackVersionDiffChangeSchema),
    warnings: z.array(z.string()).default([]),
});
export const BoardPackBuilderSchema = z.object({
    meeting: BoardMeetingSchema,
    sections: z.array(BoardPackSectionSchema),
    memos: z.array(BoardMemoSchema),
    resolutions: z.array(BoardResolutionSchema),
    papers: z.array(BoardPaperSchema),
    circulations: z.array(BoardCirculationSchema),
    minutes: BoardMinutesSchema.nullable(),
    completeness: BoardPackCompletenessSchema,
});
export const BoardPackSectionsResponseSchema = z.object({
    sections: z.array(BoardPackSectionSchema),
    completeness: BoardPackCompletenessSchema,
});
// ─── Transition ────────────────────────────────────────────
export const BoardTransitionSchema = z.object({
    targetStatus: boardMeetingStatusEnum,
});
// ─── Response ──────────────────────────────────────────────
export const BoardMeetingListResponseSchema = z.object({
    meetings: z.array(BoardMeetingSchema),
    total: z.number().int(),
});
//# sourceMappingURL=board-meetings.js.map